import React from 'react';
import UserMarkRow from './UserMarkRow';

const UserMarksList = React.memo(({
    lessons = [],
    isLoaded = false,
    lazyLoading = false,
    onLoadMore,
    user,
    indices,
    groupLesson = true,
    searchTerm = '',
    onOpenSummary,
    t
}) => {
    // 1. Loading State
    if (!isLoaded && lessons.length === 0) {
        return (
            <div
                className="theme-card summary-card p-5 text-center shadow-lg anim-fade-up"
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.6)'
                }}
            >
                <div className="display-4 text-primary mb-3">
                    <i className="fad fa-sync-alt fa-spin"></i>
                </div>
                <h4 className="font-weight-bold text-dark mb-1">
                    {t("Pages.Lessons.Loading") || 'جارٍ استرجاع سجل العلامات والدرجات...'}
                </h4>
                <div className="small text-muted">
                    {t('Pages.Lessons.PleaseWaitLoading') || 'يرجى الانتظار ريثما يتم جلب بيانات الدروس والنتائج'}
                </div>
            </div>
        );
    }

    // 2. Empty Results State
    if (isLoaded && lessons.length === 0) {
        return (
            <div
                className="theme-card summary-card p-5 text-center shadow-lg anim-fade-up"
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.6)'
                }}
            >
                <div className="display-4 text-warning mb-3">
                    <i className="fad fa-search"></i>
                </div>
                <h4 className="font-weight-bold text-dark mb-1">
                    {t("Pages.Lessons.NotFound") || 'لم يتم العثور على أي علامات أو دروس'}
                </h4>
                <p className="text-muted small mb-0">
                    {searchTerm
                        ? (t('Pages.Lessons.NoSearchResultsWithTerm') ? `${t('Pages.Lessons.NoSearchResultsWithTerm')}: "${searchTerm}"` : `لا توجد نتائج مطابقة لبحثك: "${searchTerm}" في هذه الفترة المحددة`)
                        : (t('Pages.Lessons.NoMarksPeriod') || 'لم تقم بحل أو تسجيل أي دروس في هذه الفترة الزمنية أو باستخدام الفلاتر الحالية')}
                </p>
            </div>
        );
    }

    // 3. Render Modern Interactive Table
    return (
        <div
            className="theme-card summary-card overflow-hidden shadow-lg mb-4 anim-fade-up"
            style={{
                backgroundColor: 'rgba(255, 255, 255, 0.96)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.6)'
            }}
        >
            {/* Header */}
            <div className="p-3 px-md-4 border-bottom d-flex align-items-center justify-content-between flex-wrap" style={{ backgroundColor: '#f8fafc', gap: '8px' }}>
                <div className="d-flex align-items-center">
                    <i className="fas fa-list-check text-primary ml-2" style={{ fontSize: '1.1rem' }}></i>
                    <h5 className="font-weight-bold mb-0 text-dark" style={{ fontSize: '1rem' }}>
                        {t('Pages.Lessons.MarksListHeader') || 'قائمة الدروس ونتائج المحاولات'}
                    </h5>
                </div>
                <span className="badge badge-primary px-3 py-1 font-weight-bold" style={{ borderRadius: '20px', fontSize: '0.80rem', width: 'auto' }}>
                    {lessons.length} {t('Pages.Lessons.Lessons') || 'دروس مسجلة'}
                </span>
            </div>

            {/* Interactive Table */}
            <div className="table-responsive p-0">
                <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.88rem' }}>
                    <thead className="thead-light" style={{ fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <tr>
                            <th className="text-center" style={{ width: '45px' }}>#</th>
                            <th>
                                <i className="fas fa-book-open text-primary ml-1"></i>
                                <span>{t('Pages.Lessons.LessonNameAndCategory') || 'اسم الدرس والتصنيف'}</span>
                            </th>
                            <th>
                                <i className="fas fa-award text-success ml-1"></i>
                                <span>{t('Pages.Lessons.ResultAndMastery') || 'النتيجة والإتقان'}</span>
                            </th>
                            <th>
                                <i className="fas fa-history text-info ml-1"></i>
                                <span>{t('Pages.Lessons.AttemptsAndTiming') || 'المحاولات والتوقيت'}</span>
                            </th>
                            <th className="text-center">
                                <i className="fas fa-sliders-h text-secondary ml-1"></i>
                                <span>{t('Pages.Lessons.OptionsAndSkills') || 'خيارات ونتائج المهارات'}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {lessons.map((lesson, idx) => {
                            const attemptId = lesson.AttemptId || lesson.UserLessonMarkId || lesson.Attempts?.[0]?.Id || '';
                            const uniqueKey = `lesson-mark-${lesson.Id || 'item'}-${attemptId}-${lesson.CreationDate || ''}-${idx}`;
                            return (
                                <UserMarkRow
                                    key={uniqueKey}
                                    lesson={lesson}
                                    index={idx + 1}
                                    user={user}
                                    indices={indices}
                                    groupLesson={groupLesson}
                                    onOpenSummary={onOpenSummary}
                                    t={t}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Load More Option */}
            {lazyLoading && (
                <div className="p-3 text-center border-top bg-light">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm px-4 py-2 font-weight-bold shadow-sm"
                        style={{ borderRadius: '20px', width: 'auto' }}
                        onClick={onLoadMore}
                    >
                        <i className="fas fa-chevron-down ml-1"></i>
                        <span>{t('Pages.Lessons.LoadMore') || 'تحميل المزيد من الدروس'}</span>
                    </button>
                </div>
            )}
        </div>
    );
});

export default UserMarksList;
